import { MAPBOX_TOKEN } from "../config/mapbox";
import { isValidUkPostcode, normalizeUkPostcode } from "./ukPostcode";

export interface GeocodeSuggestion {
  mapboxId: string;
  name: string;
  fullAddress: string;
  placeFormatted?: string;
}

export interface GeocodeResult {
  name: string;
  fullAddress: string;
  longitude: number;
  latitude: number;
}

const SEARCH_BOX_BASE = "https://api.mapbox.com/search/searchbox/v1";

export async function suggestAddress(
  query: string,
  options?: {
    proximity?: [number, number];
    limit?: number;
    token?: string;
  }
): Promise<GeocodeSuggestion[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const token = options?.token ?? MAPBOX_TOKEN;
  const params = new URLSearchParams({
    q: trimmed,
    access_token: token,
    limit: String(options?.limit ?? 5),
    language: "en",
    country: "GB",
  });
  if (options?.proximity) {
    params.set("proximity", `${options.proximity[0]},${options.proximity[1]}`);
  }

  const res = await fetch(`${SEARCH_BOX_BASE}/suggest?${params}`);
  if (!res.ok) throw new Error(`Mapbox suggest failed: ${res.status}`);
  const data = await res.json();
  const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];

  return suggestions.map((s: Record<string, unknown>) => ({
    mapboxId: String(s.mapbox_id ?? ""),
    name: String(s.name ?? s.name_preferred ?? "Address"),
    fullAddress: String(
      s.full_address ?? s.place_formatted ?? s.address ?? s.name ?? ""
    ),
    placeFormatted: s.place_formatted
      ? String(s.place_formatted)
      : undefined,
  }));
}

export async function retrieveAddress(
  mapboxId: string,
  options?: { token?: string }
): Promise<GeocodeResult | null> {
  if (!mapboxId) return null;
  const token = options?.token ?? MAPBOX_TOKEN;
  const params = new URLSearchParams({
    access_token: token,
  });

  const res = await fetch(
    `${SEARCH_BOX_BASE}/retrieve/${encodeURIComponent(mapboxId)}?${params}`
  );
  if (!res.ok) throw new Error(`Mapbox retrieve failed: ${res.status}`);
  const data = await res.json();
  const feature = data.features?.[0];
  if (!feature?.geometry?.coordinates) return null;

  const [longitude, latitude] = feature.geometry.coordinates as [
    number,
    number,
  ];
  const props = feature.properties ?? {};

  return {
    name: String(props.name ?? props.name_preferred ?? "Address"),
    fullAddress: String(
      props.full_address ??
        props.place_formatted ??
        feature.place_name ??
        props.name ??
        ""
    ),
    longitude,
    latitude,
  };
}

/** Geocode a UK postcode directly (no autocomplete selection required). */
export async function geocodePostcode(
  postcode: string,
  options?: { token?: string; proximity?: [number, number] }
): Promise<GeocodeResult | null> {
  const normalized = normalizeUkPostcode(postcode);
  if (!isValidUkPostcode(normalized)) return null;

  const token = options?.token ?? MAPBOX_TOKEN;
  const params = new URLSearchParams({
    q: normalized,
    access_token: token,
    limit: "5",
    language: "en",
    country: "GB",
  });
  if (options?.proximity) {
    params.set("proximity", `${options.proximity[0]},${options.proximity[1]}`);
  }

  // Search Box /suggest requires a session token; /forward works for one-shot geocoding.
  const res = await fetch(`${SEARCH_BOX_BASE}/forward?${params}`);
  if (!res.ok) {
    throw new Error(`Mapbox forward failed: ${res.status}`);
  }
  const data = await res.json();
  const features = Array.isArray(data.features) ? data.features : [];
  const compact = normalized.replace(/\s+/g, "");

  const match =
    features.find((feature: Record<string, unknown>) => {
      const props = (feature.properties ?? {}) as Record<string, unknown>;
      const name = String(props.name ?? props.name_preferred ?? "")
        .toUpperCase()
        .replace(/\s+/g, "");
      return name === compact;
    }) ??
    features.find((feature: Record<string, unknown>) => {
      const props = (feature.properties ?? {}) as Record<string, unknown>;
      return props.feature_type === "postcode";
    }) ??
    features[0];

  if (!match?.geometry?.coordinates) return null;

  const [longitude, latitude] = match.geometry.coordinates as [
    number,
    number,
  ];
  const props = (match.properties ?? {}) as Record<string, unknown>;

  return {
    name: String(props.name ?? props.name_preferred ?? normalized),
    fullAddress: String(
      props.full_address ??
        props.place_formatted ??
        match.place_name ??
        props.name ??
        normalized
    ),
    longitude,
    latitude,
  };
}

/** Resolve coordinates from a UK postcode or legacy Mapbox id. */
export async function resolveLocation(
  input: string,
  existingMapboxId?: string,
  options?: { token?: string; proximity?: [number, number] }
): Promise<GeocodeResult | null> {
  const trimmed = input.trim();
  if (isValidUkPostcode(trimmed)) {
    return geocodePostcode(trimmed, options);
  }
  if (existingMapboxId) {
    return retrieveAddress(existingMapboxId, options);
  }
  return null;
}
